import { useMemo } from 'react';
import styled from '@emotion/styled';
import { AudioPlayer } from '@trinity/chat-components/components/AudioPlayer';
import { VideoPlayer } from '@trinity/chat-components/components/VideoPlayer';
import { AudioPlayerContainer } from '@trinity/chat-components/containers/AudioPlayer';
import type { SetMediaRef } from './interfaces';

type Props = {
  disableMediaControls?: boolean;
  setMediaRef: SetMediaRef;
  speaker?: {
    start: number;
    end: number;
  };
  media: {
    clipUrl?: string;
    type: 'audio' | 'video' | null;
    url: string | null;
  };
  mediaSetting: 'audio' | 'video' | null;
};

export const WidgetQuoteCardMedia = (props: Props) => {
  const url = useMemo(() => {
    return props.media?.clipUrl || props.media?.url || null;
  }, [props.media]);

  const ready = useMemo(() => {
    if (!url || !props.mediaSetting) return false;
    if (props.mediaSetting === 'video' && props.media?.type === 'audio') return false;
    return true;
  }, [props.media, props.mediaSetting, url]);

  const audioClipRange = useMemo(() => {
    if (!Number.isFinite(props.speaker?.start) || !Number.isFinite(props.speaker?.end)) {
      return null;
    }

    const start = Math.max(0, Number(props.speaker?.start));
    const end = Math.max(0, Number(props.speaker?.end));

    if (end <= start) {
      return null;
    }

    return { start, end };
  }, [props.speaker?.end, props.speaker?.start]);

  if (!ready) return null;

  return (
    <Root>
      {props.mediaSetting === 'audio' && (
        <AudioPlayerContainer>
          <AudioPlayer audio={{ src: url }} clipStart={audioClipRange?.start} clipEnd={audioClipRange?.end} />
        </AudioPlayerContainer>
      )}
      {props.mediaSetting === 'video' && (
        <Video>
          <VideoPlayer
            ref={props.setMediaRef}
            controls={!props.disableMediaControls}
            style={{ maxHeight: '120px', width: `calc(${120 * (16 / 9)}px)` }}
            src={url}
          />
        </Video>
      )}
    </Root>
  );
};

WidgetQuoteCardMedia.displayName = 'Widget.Quote.Card.Media';

const Root = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
`;

const Video = styled.div`
  max-height: 120px;
`;
